import { useZero } from "@rocicorp/zero/react";
import { useRef, useState } from "react";
import { GRANT_REQUEST_NOTE_MAX_LENGTH } from "../../shared/grantRequests";
import { sharedMutators } from "../../shared/mutators";
import { classifyZeroResult } from "../lib/zeroResultError";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { NoteInput } from "./NoteInput";
import { WarningContent, WarningFooter } from "./ScopeAccessCloseWarning";
import { Text } from "./Text";

type Props = {
  requestId: string;
  connectorName: string;
  onClose: () => void;
  onDeclined: () => void;
};

export function DeclineGrantRequestModal({
  requestId,
  connectorName,
  onClose,
  onDeclined,
}: Props) {
  const z = useZero();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decline = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const result = await z.mutate(
        sharedMutators.dismissGrantRequest({
          requestId,
          note: note.trim() || undefined,
        })
      ).server;
      if (result.type === "error") {
        const decision = classifyZeroResult(result);
        setError(
          (decision.kind === "toast" && decision.message) ||
            "Couldn’t decline this request. Try again."
        );
      } else {
        onDeclined();
      }
    } catch {
      setError("Couldn’t decline this request. Try again.");
    }
    setPending(false);
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!pending) onClose();
      }}
      alert
      initialFocusRef={inputRef}
    >
      <WarningContent>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Decline share request?
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            The requested access to {connectorName} won’t be granted.
          </Text>
        </div>
        <NoteInput
          inputRef={inputRef}
          label="Decline reason"
          hideLabel
          helperText="Optional. Shared with the requester."
          placeholder="Explain why this request was declined…"
          maxLength={GRANT_REQUEST_NOTE_MAX_LENGTH}
          value={note}
          onChange={setNote}
          disabled={pending}
        />
        {error && (
          <div role="alert">
            <Text variant="text-small" color="var(--text-danger)">
              {error}
            </Text>
          </div>
        )}
        <WarningFooter>
          <Button
            size="l"
            variant="secondary"
            onClick={onClose}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            size="l"
            variant="danger"
            onClick={decline}
            disabled={pending}
          >
            Decline request
          </Button>
        </WarningFooter>
      </WarningContent>
    </Modal>
  );
}
